import {shallowEqual} from 'react-redux';
import {createSelector} from 'reselect';

import {useWorkspaceRefsContext} from '../../components/Workspace/WorkspaceRefsContext';
import {type RootState} from '../../types/redux';
import {getPrefixSectionName} from '../../util/panelbank';
import {
  PANEL_BANK_CHARTS_NAME,
  PANEL_BANK_CUSTOM_CHARTS_NAME,
  PANEL_BANK_CUSTOM_VISUALIZATIONS_NAME,
  PANEL_BANK_EVALUATION_NAME,
  PANEL_BANK_EVALUATION_TABLES_LEGACY_NAME,
  PANEL_BANK_EVALUATION_TABLES_NAME,
  PANEL_BANK_HIDDEN_SECTION_NAME,
  PANEL_BANK_MEDIA_NAME,
  PANEL_BANK_SYSTEM_NAME,
  PANEL_BANK_TABLES_NAME,
} from '../../util/panelbankTypes';
import {getMetrics} from '../../util/panels';
import {type LayedOutPanel} from '../../util/panelTypes';
import {useSelector} from '../hooks';
import {makeSectionRefsSelector} from '../views/panelBankConfig/selectors';

export function usePinnedSectionPrefixes(): string[] {
  const {panelBankConfigRef} = useWorkspaceRefsContext();
  const selector = createSelector(
    makeSectionRefsSelector(panelBankConfigRef),
    (state: RootState) => state.views.parts['panel-bank-section-config'],
    (state: RootState) => state.views.parts.panel,
    (sectionRefs, sectionParts, panelParts) =>
      getPinnedSectionPrefixes(
        sectionRefs.map(sectionRef => {
          const section = sectionParts[sectionRef.id];
          return {
            defaultName: section.defaultName,
            pinned: section.pinned,
            panels: section.panelRefs.map(panelRef => panelParts[panelRef.id]),
          };
        })
      )
  );
  return useSelector(selector, shallowEqual);
}

/** Default sections that are not backed by a summary-metric key prefix. */
const NON_PREFIX_DEFAULT_SECTION_NAMES = new Set([
  PANEL_BANK_CHARTS_NAME,
  PANEL_BANK_MEDIA_NAME,
  PANEL_BANK_TABLES_NAME,
  PANEL_BANK_SYSTEM_NAME,
  PANEL_BANK_HIDDEN_SECTION_NAME,
  PANEL_BANK_CUSTOM_VISUALIZATIONS_NAME,
  PANEL_BANK_EVALUATION_TABLES_LEGACY_NAME,
  PANEL_BANK_EVALUATION_TABLES_NAME,
  PANEL_BANK_EVALUATION_NAME,
  PANEL_BANK_CUSTOM_CHARTS_NAME,
]);

/**
 * Derives `sectionAwareHistoryKeys.requiredPrefixes` from pinned workspace sections.
 * Only prefix-based sections are included. Non-prefixed (type-based) sections
 * (i.e. Charts/Media/Tables) are excluded because SAHK always returns those.
 */
export function getPinnedSectionPrefixes(
  sections: Array<{
    defaultName?: string;
    pinned: boolean;
    panels?: ReadonlyArray<LayedOutPanel>;
  }>
): string[] {
  const prefixes = new Set<string>();
  for (const section of sections) {
    if (!section.pinned) {
      continue;
    }

    if (
      section.defaultName &&
      NON_PREFIX_DEFAULT_SECTION_NAMES.has(section.defaultName)
    ) {
      continue;
    }

    if (section.defaultName) {
      prefixes.add(section.defaultName);
    }

    for (const panel of section.panels ?? []) {
      if (panel.isAuto === false) {
        continue;
      }
      for (const metric of getMetrics(panel)) {
        const prefix = getPrefixSectionName(metric);
        if (prefix != null) {
          prefixes.add(prefix);
        }
      }
    }
  }
  return [...prefixes].sort();
}
